/**
 * This store registry logic is required to ensure that we only initialize the store once per component instance in an SSR friendly way
 *
 * useMemo unfortunately will re-run in concurrent mode, and cause us to create the store multiple times.
 * useMemo also does not listen to changes to useRef so would always run twice even if we tried to update a ref to say it has already been run
 *
 * We can't useLayoutEffect or useEffect either as this will cause SSR to fail
 *
 * This leaves us with the only option of storing refs outside of react.
 *
 * Usage:
 * - Create a ref via useKeyedRef
 * - Use useStableInitialization and pass the ref from useKeyedRef to ensure that the store is only initialized once per component instance
 * - Use toBeCalledOnceFor with the above store to ensure that a function is only called once per store
 */

import { useEffect, useMemo, useState } from 'react';
import type { Store } from 'redux';
import noop from 'lodash/noop';

type KeyedRef = { [uniqueId: string]: string };
const keyedRefs: { [uniqueId: string]: KeyedRef | undefined } = {};

let keyedRefPrefixId = 0;

const keyedRefPrefixIdSingleton = {
	keyedRefPrefixIdRegistry: new WeakMap<Function, number>(),
};

export const getUniqueKeyedId = (createStoreFunc: Function, key: string): string => {
	if (!keyedRefPrefixIdSingleton.keyedRefPrefixIdRegistry.has(createStoreFunc)) {
		keyedRefPrefixIdSingleton.keyedRefPrefixIdRegistry.set(createStoreFunc, keyedRefPrefixId++);
	}
	return `${String(keyedRefPrefixIdSingleton.keyedRefPrefixIdRegistry.get(createStoreFunc))}_${key}`;
};

/**
 *
 * Unfortunately, useRef was not stable enough for our use case in concurrent mode
 * We need to use refs outside of react to ensure a stable reference between concurrent renders eg in strict mode
 */
export const useKeyedRef = (createStoreFunc: Function, key: string) => {
	const uniqueId = getUniqueKeyedId(createStoreFunc, key);
	if (!keyedRefs[uniqueId]) {
		keyedRefs[uniqueId] = { key };
	}

	useEffect(() => {
		keyedRefs[uniqueId] = undefined;
	}, [uniqueId]);

	return keyedRefs[uniqueId];
};

const stableRefRegistrySingleton = {
	stableRefRegistry: new WeakMap(),
};

/**
 * A special function to ease feature flagging of store initialization
 *
 */
export const useStableInitialization = <T,>(
	ref: KeyedRef | undefined,
	factoryFunction: () => T,
	shouldUseStable: boolean, // to help feature gate from useMemo to useStable
): [T, () => void] => {
	const useStableFactory = () => {
		const { stableRefRegistry } = stableRefRegistrySingleton;
		// A localRef will ensure better garbage collection in the WeakMap
		const [localRef] = useState<KeyedRef>(() => ref || { no: 'none' });
		if (!stableRefRegistry.has(localRef)) {
			stableRefRegistry.set(localRef, factoryFunction());
		}
		const dispose = () => {
			if (stableRefRegistry.has(localRef)) {
				stableRefRegistry.delete(localRef);
			}
		};
		const result: [T, () => void] = [stableRefRegistry.get(localRef), dispose];
		return result;
	};

	const useMemoryFactory = () => useMemo((): [T, () => void] => [factoryFunction(), noop], []);
	const factoryMethod = shouldUseStable
		? // use setState as it preserved value between concurrent renders
			useStableFactory
		: // use memo can be called multiple times for concurrent mode and is unsafe for side-effects
			useMemoryFactory;
	return factoryMethod();
};

const storeInitializationRegistrySingleton = {
	storeInitializationRegistry: new WeakSet<Store<unknown>>(),
};

export const toBeCalledOnceFor = <T,>(callingFor: Store<T>, cb: () => void) => {
	// this function contains a side effect and cannot be called once for a given store
	const storeInitializationRegistry =
		storeInitializationRegistrySingleton.storeInitializationRegistry;
	if (storeInitializationRegistry.has(callingFor)) {
		// already initialized
		return;
	}
	storeInitializationRegistry.add(callingFor);
	cb();
};

export const toBeCalledOnceForCleanup = <T,>(callingFor: Store<T>): void => {
	const storeInitializationRegistry =
		storeInitializationRegistrySingleton.storeInitializationRegistry;
	if (storeInitializationRegistry.has(callingFor)) {
		storeInitializationRegistry.delete(callingFor);
	}
};

export const resetStoreRegistriesForTesting = () => {
	// keyedRefs
	Object.keys(keyedRefs).forEach((key) => {
		keyedRefs[key] = undefined;
	});

	// storeInitializationRegistry
	storeInitializationRegistrySingleton.storeInitializationRegistry = new WeakSet<Store<unknown>>();

	// stableRefRegistry
	stableRefRegistrySingleton.stableRefRegistry = new WeakMap();

	// keyedRefUniquePrefixRegistry
	keyedRefPrefixId = 0;
	keyedRefPrefixIdSingleton.keyedRefPrefixIdRegistry = new WeakMap<Function, number>();
};
